import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { reactive, toRefs, computed } from 'vue'

import { Claim } from '@/domain/pio/Claim'
import {
  Cover,
  PetV2,
  PolicyV2,
  Customer,
  PolicyLimitsV2CoverageLimit,
} from '@/domain/pio/Policy'
import { getExistingClaims } from '@/lib/claim'
import {
  getCoverages,
  getVetFeesCoverages,
  allSubcoverages,
  getTrackableSubcoverages,
} from '@/lib/coverage'
import { findPolicies, getPolicyDetails } from '@/lib/policy'
import { getPolicyV2 } from '@/services/pio/policyService'

type SetPolicyDetailsPayload = {
  policyId?: string
  policyRef?: string
  petUuid: string
  startDate?: string
  endDate?: string
}

export const usePolicyStore = defineStore('policy', () => {
  // ----- State

  const state = reactive({
    selectedPolicy: <PolicyV2 | undefined>undefined,
    selectedPet: <PetV2 | undefined>undefined,
    customer: <Customer | undefined>undefined,
    cover: <Cover | undefined>undefined,
    limits: <PolicyLimitsV2CoverageLimit | undefined>undefined,
    existingClaims: <Claim[]>[],
  })

  // ----- Getters

  const coverages = computed(() => {
    return getCoverages(state.limits)
  })

  // Returns subcoverages that we allow and limits are more than 0
  const trackableSubcoverages = computed(() => {
    return getTrackableSubcoverages(state.limits)
  })

  const uncoveredSubcoverages = computed(() => {
    const vetFeesCoverage = getVetFeesCoverages(state.limits)
    const vetFeesSubcoverages = vetFeesCoverage?.subcoverages.map(
      (subcoverage) => subcoverage.name
    )
    return allSubcoverages.filter(
      ({ subcoverage }) => !vetFeesSubcoverages?.includes(subcoverage)
    )
  })

  const isNewProduct = computed(() => {
    const newProductNames = [
      'essential care',
      'standard care',
      'standard care pre-existing', // This one is redundant but good to have for clarity
      'complete care',
    ]

    return newProductNames.some((product) =>
      state.selectedPolicy?.product?.toLowerCase()?.startsWith(product)
    )
  })

  // ----- Actions

  const selectPolicy = (policy?: PolicyV2) => {
    state.selectedPolicy = policy
  }

  const setPolicyDetails = async ({
    policyId,
    policyRef,
    petUuid,
    startDate,
    endDate,
  }: SetPolicyDetailsPayload) => {
    if (policyRef) {
      const { items: policies } = await findPolicies({
        field: policyRef,
        type: 'policyNumber',
      })

      if (policies.length === 0) {
        throw new Error(`No policies found for ref ${policyRef}`)
      }

      policies.find((policy) => {
        const pet = policy.pets.find((pet) => pet.uuid === petUuid)
        if (pet) {
          policyId = pet.policy_id
        }
      })

      if (!policyId) {
        throw new Error(`Policy id not found in returned pets for ${policyRef}`)
      }
    }

    //No need to call if policy is provided already
    if (!state.selectedPolicy) {
      if (!policyId) {
        throw new Error('Policy id not provided')
      }
      if (!startDate) {
        throw new Error('Start date not provided')
      }

      state.selectedPolicy = await getPolicyV2(policyId, {
        policy_at_date: dayjs(startDate).format('YYYY-MM-DD'),
      })
    }

    const { cover, customer, limits, pet } = await getPolicyDetails(
      state.selectedPolicy,
      petUuid,
      startDate,
      endDate
    )
    state.selectedPet = pet
    state.cover = cover
    state.customer = customer
    state.limits = limits
    state.existingClaims = []
  }

  const getExistingClaimsForSelectedPet = async () => {
    if (!state.selectedPet) {
      throw new Error('No policy selected')
    }

    state.existingClaims = await getExistingClaims({
      policyId: state.selectedPet.policy_id,
      insuredEntityId: state.selectedPet.uuid,
    })

    return state.existingClaims
  }

  const reset = () => {
    state.cover = undefined
    state.customer = undefined
    state.limits = undefined
    state.selectedPet = undefined
    state.selectedPolicy = undefined
    state.existingClaims = []
  }

  return {
    ...toRefs(state),
    coverages,
    trackableSubcoverages,
    uncoveredSubcoverages,
    isNewProduct,
    selectPolicy,
    setPolicyDetails,
    getExistingClaimsForSelectedPet,
    reset,
  }
})
